/** Character preview: the real paper ninja and shared gameplay accessories. */
import { clonePoses, defaultFeel, defaultPoses, NEON, SilhouetteRenderer, ViewState } from "@lache/render";
import { type CourseData, createInputFrame, defaultTuning, Sim } from "@lache/sim";
import { type Container, Graphics, type Sprite } from "pixi.js";
import type { Appearance } from "./appearance";
import { CharacterAppearanceRenderer } from "./appearance-renderer";
import { MiniStage } from "./stage";

const PREVIEW_FLOOR: CourseData = {
  version: 1,
  name: "appearance-preview",
  start: { x: 0, y: 0 },
  respawns: [{ x: 0, y: 0 }],
  marks: [],
  entities: [{ type: "solid", x: -10, y: -1, w: 20, h: 1 }],
};
const VERTICAL_SPAN = 2.55;

export type Celebration = "victory-pump" | "flex" | "fist-pump";

/** A host can reuse the figure on one shared renderer without creating another canvas. */
export interface SharedAppearanceScene {
  stage: MiniStage;
  world: Container;
  celebration: Celebration;
}

export class AppearancePreview {
  private readonly stage: MiniStage;
  private readonly sim = new Sim(PREVIEW_FLOOR, defaultTuning);
  private readonly input = createInputFrame();
  private readonly view = new ViewState();
  private readonly poses = clonePoses(defaultPoses);
  private readonly feel = { ...defaultFeel, bandWidth: 0, ribbonWidth: 0, bodyGlow: 0, contactAlpha: 0 };
  private body: SilhouetteRenderer | null = null;
  private accessories: CharacterAppearanceRenderer | null = null;
  private bodyDrawn = false;
  private shadow: Sprite | null = null;
  private initializing: Promise<void> | null = null;
  private destroyed = false;
  private reducedMotion = false;
  private closeUp = false;
  private framingDirty = true;
  private dirty = true;
  private time = 0;
  private appearance: Appearance = {
    hair: "crop",
    hairColor: "#30313d",
    headband: "classic",
    headbandColor: "#ff8c1a",
    hat: "none",
  };

  constructor(
    private readonly parent: HTMLElement,
    private readonly sharedScene?: SharedAppearanceScene,
  ) {
    this.stage =
      sharedScene?.stage ??
      new MiniStage({
        parent,
        resizeTo: parent,
        className: "appearance-canvas",
        resolution: Math.min(2, window.devicePixelRatio || 1),
      });
  }

  async init(): Promise<void> {
    if (this.initializing) return this.initializing;
    if (this.destroyed) return;
    this.initializing = this.initialize();
    return this.initializing;
  }

  private async initialize(): Promise<void> {
    if (!this.sharedScene) await this.stage.init();
    if (this.destroyed) {
      if (!this.sharedScene) this.disposeStage();
      return;
    }
    const factory = this.stage.shapes;
    const world = this.sharedScene?.world ?? this.stage.viewport.world;
    this.shadow = factory.disc(world, 0, 0.018, 0.4, NEON.ink, 0.26);
    this.shadow.width = 0.9;
    this.shadow.height = 0.095;
    this.body = new SilhouetteRenderer(factory, this.poses);
    world.addChild(this.body.root);
    this.accessories = new CharacterAppearanceRenderer(factory, world, this.body);
    this.accessories.setAppearance(this.appearance);
    if (this.sharedScene) {
      // Settle the idle ground contact once; the podium then animates render poses only.
      for (let i = 0; i < 4; i++) this.sim.step(this.input);
      this.drawCelebration(0, 0);
    } else {
      this.stage.app.canvas.setAttribute("aria-label", "Live ninja appearance preview");
      this.stage.app.canvas.setAttribute("role", "img");
      this.update(performance.now());
    }
  }

  setAppearance(value: Appearance): void {
    this.appearance = { ...value };
    this.dirty = true;
    if (!this.destroyed) this.accessories?.setAppearance(value);
  }

  setReducedMotion(value: boolean): void {
    if (this.reducedMotion === value) return;
    this.reducedMotion = value;
    this.dirty = true;
    if (this.sharedScene) this.bodyDrawn = false;
    else this.stage.resetClock();
  }

  setCloseUp(value: boolean): void {
    if (this.closeUp === value) return;
    this.closeUp = value;
    this.framingDirty = true;
    this.dirty = true;
  }

  /** Frame loop belongs to the prototype page; hidden previews should call pause instead. */
  update(nowMs: number): void {
    if (this.sharedScene || this.destroyed || !this.stage.ready || !this.body) return;
    const width = this.parent.clientWidth;
    const height = this.parent.clientHeight;
    if (width < 1 || height < 1) {
      this.pause();
      return;
    }
    const span = this.closeUp ? Math.max(1.35, (1.6 * height) / width) : VERTICAL_SPAN;
    const across = (Math.max(width, height) / height) * span;
    const resized = this.stage.fit(across, this.feel);
    if (this.framingDirty && !resized) {
      this.stage.viewport.setRect(0, 0, this.stage.width, this.stage.height, across, this.feel, false);
    }
    this.framingDirty = false;
    if (this.reducedMotion && !this.dirty && !resized) return;
    const clock = this.stage.clock(nowMs);
    const dt = this.reducedMotion ? 0 : Math.min(clock.dt, 1 / 30);
    if (!this.reducedMotion) this.time += dt;
    const viewport = this.stage.viewport;
    viewport.camera.snapTo(this.closeUp ? -0.14 : -0.04, this.closeUp ? 1.55 : VERTICAL_SPAN / 2 - 0.2);
    viewport.applyCamera();
    this.drawFigure(dt, clock.ticks, clock.alpha);
    this.stage.render();
    this.dirty = false;
  }

  /** Draw into a shared scene; its host owns layout, clock, and the single render call. */
  drawCelebration(time: number, dt: number): void {
    if (!this.sharedScene || this.destroyed || !this.stage.ready || !this.body) return;
    this.time = time;
    this.drawFigure(dt, 0, 1);
    this.dirty = false;
  }

  private drawFigure(dt: number, ticks: number, alpha: number): void {
    if (!this.body) return;
    if (!this.reducedMotion) {
      for (let i = 0; i < ticks; i++) this.sim.step(this.input);
    }
    const idle = this.poses.idle;
    if (this.sharedScene) {
      this.celebrationPose(this.reducedMotion ? 0.8 : this.time, this.sharedScene.celebration);
    } else if (!this.reducedMotion) {
      const breath = Math.sin(this.time * 1.45);
      const sway = Math.sin(this.time * 0.78);
      idle.hipY = defaultPoses.idle.hipY;
      idle.torso = defaultPoses.idle.torso + 2.1 * sway + 0.35 * breath;
      idle.head = defaultPoses.idle.head - 1.5 * sway + 0.45 * Math.sin(this.time * 0.91 + 0.5);
      idle.shoulderL = defaultPoses.idle.shoulderL + 2.8 * Math.sin(this.time * 1.1 + 0.6);
      idle.shoulderR = defaultPoses.idle.shoulderR + 2.2 * Math.sin(this.time * 1.1 + 1.4);
      idle.elbowL = defaultPoses.idle.elbowL + 1.2 * breath;
      idle.elbowR = defaultPoses.idle.elbowR - 0.8 * breath;
    }
    const poseChanged = dt > 0 || !this.bodyDrawn;
    if (poseChanged) {
      this.view.update(this.sim.prev, this.sim.state, alpha, this.sim.tuning, this.feel, dt);
      if (this.sharedScene && !this.bodyDrawn) this.view.snapped = true;
      // Shared cloth Verlet state can advance at dt=0. Frozen recolors/resizes retain sprites.
      this.body.draw(this.view, this.sim, this.accessories?.drawFeel(this.feel) ?? this.feel, dt);
      this.bodyDrawn = true;
    }
    this.accessories?.draw(this.feel, dt, this.time);
  }

  private celebrationPose(time: number, move: Celebration): void {
    const p = this.poses.idle;
    const sway = Math.sin(time * 1.5);
    p.hipY = defaultPoses.idle.hipY;
    p.torso = 1.4 * sway;
    p.head = -2 - 1.4 * sway;
    if (move === "victory-pump") {
      const lift = 0.5 + 0.5 * Math.sin(time * 4.7);
      p.shoulderL = -98 - 54 * lift;
      p.shoulderR = 98 + 54 * lift;
      p.elbowL = -72 + 57 * lift;
      p.elbowR = 72 - 57 * lift;
      p.head = -5;
    } else if (move === "flex") {
      const flex = Math.sin(time * 2.4);
      p.shoulderL = -90 - 5 * flex;
      p.shoulderR = 90 + 5 * flex;
      p.elbowL = -80 - 5 * flex;
      p.elbowR = 80 + 5 * flex;
      p.torso = -2 + sway;
    } else {
      const lift = 0.5 + 0.5 * Math.sin(time * 3.8 + 1.3);
      p.shoulderL = -27;
      p.elbowL = -122;
      p.shoulderR = 125 + 34 * lift;
      p.elbowR = 44 - 34 * lift;
      p.torso = -3 + 2 * sway;
      p.head = 3 - 2 * sway;
    }
  }

  pause(): void {
    if (!this.sharedScene) this.stage.resetClock();
  }

  /** Read-only visual proof hooks; never part of game or saved player state. */
  get metadata() {
    const points = this.body?.solver.points;
    let sceneNodes = 0;
    let graphicsInstructions = 0;
    const count = (node: Container): void => {
      sceneNodes++;
      if (node instanceof Graphics) graphicsInstructions += node.context.instructions.length;
      for (const child of node.children) count(child);
    };
    if (this.stage.ready && !this.destroyed) count(this.sharedScene?.world ?? this.stage.app.stage);
    return {
      ready: this.stage.ready && !this.destroyed,
      reducedMotion: this.reducedMotion,
      closeUp: this.closeUp,
      appearance: { ...this.appearance },
      time: this.time,
      headX: points?.headX ?? 0,
      headY: points?.headY ?? 0,
      footLX: points?.footLX ?? 0,
      footLY: points?.footLY ?? 0,
      footRX: points?.footRX ?? 0,
      footRY: points?.footRY ?? 0,
      handLX: points?.handLX ?? 0,
      handLY: points?.handLY ?? 0,
      handRX: points?.handRX ?? 0,
      handRY: points?.handRY ?? 0,
      move: this.sharedScene?.celebration ?? "idle",
      strandCount: this.accessories?.metadata.strandCount ?? 0,
      sceneNodes,
      graphicsInstructions,
    };
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.sharedScene) {
      this.sharedScene.world.destroy({ children: true });
      this.disposeDetached();
    } else if (this.stage.ready) this.disposeStage();
    else if (!this.initializing) this.disposeDetached();
  }

  private disposeStage(): void {
    const factory = this.stage.shapes;
    // The glow is lazy and exists only after the body is built. Never allocate it on a dead renderer.
    const glow = this.body ? factory.glow : null;
    this.stage.app.destroy({ removeView: true }, { children: true, texture: false, textureSource: false });
    factory.circle.destroy(true);
    factory.ring.destroy(true);
    glow?.destroy(true);
    this.disposeDetached();
  }

  private disposeDetached(): void {
    this.accessories?.destroy();
  }
}
